import {
	FacebookLoginProvider,
	GoogleLoginProvider,
	SocialAuthService,
	SocialUser,
} from '@abacritt/angularx-social-login';
import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import {
	InitialiseAuthToken,
	NoAuthTokenRetrieved,
	SessionExpireDialogClosed,
	SetAuthTokenToStore,
	ValidateAuthToken,
	ValidateAuthTokenSuccess,
} from '@biz-action/system-actions/auth-token.actions';
import { Logout } from '@biz-action/user-actions/app.actions';
import { authentication } from '@biz-action/user-actions/auth-login.actions';
import { AuthenticationToken } from '@biz-selectors/app.selectors';
import { base_url, redirect_url, subdomain } from '@env/environment';
// import { AUTH_PORTAL_URL } from '@env/environment';
import { Actions, concatLatestFrom, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { getRouterSelectors as getSelectors } from '@ngrx/router-store';
import { Action, Store } from '@ngrx/store';

import { of, withLatestFrom, filter, mergeMap, combineLatest, concat, iif } from 'rxjs';
import { catchError, first, map, switchMap, take, tap } from 'rxjs/operators';
import { LoginService } from 'src/app/services/login.service';
import { currentSubdomain, l7_token, onLinking } from '../feature.selectors';
import { LoginRespModel } from '@biz-models/network/login-authenticate-network.model';
import { TenantAccount } from '@biz-models/shared/current-user.model';
import { authenticationAccount } from '@biz-action/user-actions/account-actions';
import { SubdomainList } from 'src/app/select-subdomain/selectors';
import { SnackbarErrorComponent } from '@biz-ui/shared-presenters/custom-snackbar/snackbar-error/snackbar-error.component';

@Injectable({
	providedIn: 'root',
})
export class AuthTokenEffects implements OnInitEffects {
	constructor(
		private actions$: Actions,
		private store: Store,
		private loginService: LoginService,
		private router: Router,
		private authService: SocialAuthService,
		private _snackBar: MatSnackBar,
	) {}
	readonly routerSelectors = getSelectors();
	readonly subdomain$ = this.store.select(currentSubdomain);
	readonly subdomainInLocalStore$ = of(localStorage.getItem('subdomain'));;
	// readonly subdomain$ = of('');
	readonly tokenInLocalStore$ = of(localStorage.getItem('l7_token'));
	readonly tokenInUrl$ = this.store.select(this.routerSelectors.selectUrl).pipe(
		filter((url) => url !== undefined),
		mergeMap((url) =>
			this.store.select(this.routerSelectors.selectQueryParam('token')).pipe(map((token) => token || '')),
		),
	);

	readonly adminMode$ = this.store.select(this.routerSelectors.selectUrl).pipe(
		filter((url) => url !== undefined),
		mergeMap((url) =>
			this.store.select(this.routerSelectors.selectQueryParam('adminMode')).pipe(map((mode) => mode || '')),
		),
	);
	readonly subdomainType$ = this.store.select(this.routerSelectors.selectUrl).pipe(
		filter((r) => !!r),
		mergeMap((r) =>
			this.store
				.select(this.routerSelectors.selectQueryParam('_subdomain'))
				.pipe(map((_subdomain) => _subdomain || '')),
		),
	);

	ngrxOnInitEffects() {
		return InitialiseAuthToken();
	}

	authEffectInitiased$ = createEffect(() =>
		this.actions$.pipe(
			ofType(InitialiseAuthToken),
			switchMap(() =>
				combineLatest([this.tokenInLocalStore$, this.tokenInUrl$, this.adminMode$, this.subdomainType$]).pipe(
					take(1),
				),
			),

			map(([tokenFromAction, tokenFromUrl, mode, subdomain]) => {
				if (mode) {
					const queryParams = {
						_subdomain: subdomain,
					};
					localStorage.removeItem('l7_token');
					localStorage.removeItem('token');
					localStorage.removeItem('subdomain');
					this.router.navigate(['/login'], { queryParams });
				} else {
					return tokenFromUrl || tokenFromAction;
				}
			}),
			filter((token) => !!token),
			map((token) => (token ? SetAuthTokenToStore(token) : NoAuthTokenRetrieved())),
		),
	);

	onSetAuthtoken$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SetAuthTokenToStore),
			map((action) => action.payload.token),
			map((token) => {
				if (token) return ValidateAuthToken(token);
				else return NoAuthTokenRetrieved();
			}),
		),
	);

	validateAuthenticationToken$ = createEffect(() =>
		this.actions$.pipe(
			ofType(ValidateAuthToken),
			map((action) => action.payload.token),
			filter((token) => /^\d+\|.+$/.test(token)),
			map(() => ValidateAuthTokenSuccess()),
		),
	);

	setTokenToLocalStorage$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(ValidateAuthTokenSuccess),
				withLatestFrom(this.store.select(AuthenticationToken)),
				map(([action, token]) => {
					localStorage.setItem('l7_token', token);
					localStorage.setItem('token', token);
				}),
			),
		{ dispatch: false },
	);

	redirectToLogin$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(
					NoAuthTokenRetrieved,
					SessionExpireDialogClosed,
					// authentication.logoutaccountsuccesss,
					// authentication.logoutaccountfail,
				),
				// concatLatestFrom(() => this.store.select(getSelectors().selectCurrentRoute).pipe(filter(r => !!r))),
				switchMap(() => this.store.select(getSelectors().selectCurrentRoute).pipe(first((r) => !!r))),
				filter((route) => !['login', 'logout', 'register'].includes(route.routeConfig.path)),
				tap(() => this.router.navigateByUrl('/logout' + window.location.search)),
			),
		{ dispatch: false },
	);

	onLogout$ = createEffect(() =>
		this.actions$.pipe(
			ofType(authentication.logoutaccount),
			concatLatestFrom(() => [this.subdomainType$, this.subdomainInLocalStore$]),
			switchMap(([, subdomain, localSubdomain]) =>
				iif(
					() => !!subdomain && subdomain == localSubdomain,
					this.loginService.logOut(null).pipe(map(() => subdomain)),
					of(subdomain),
				),
			),
			map((resp) => authentication.logoutaccountsuccesss(resp)),
			catchError((err) => of(authentication.logoutaccountfail(err))),
		),
	);

	onSignIn$ = createEffect(() =>
		this.actions$.pipe(
			ofType(authentication.logininit),
			switchMap((action) =>
				this.loginService.login(action.payload).pipe(
					map((resp) => authentication.loginsuccess(resp.data)),
					catchError((err) => of(authentication.loginfail(err.message))),
				),
			),
		),
	);

	onSignInSetting$ = createEffect(() =>
		this.actions$.pipe(
			ofType(authentication.settingslogininit),
			switchMap((action) =>
				this.loginService.login(action.payload).pipe(
					map((resp) => authentication.settingloginsuccess(resp.data)),
					catchError((err) => of(authentication.loginfail(err.message))),
				),
			),
		),
	);

	setTokenOnLogon$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(authentication.loginsuccess),
			concatLatestFrom(() => this.store.select(onLinking)),
			filter(([, r]) => !r),
			tap(([action]) => {
				localStorage.setItem('token', action.payload[0].token);
				localStorage.setItem('subdomain', action.payload[0].subdomain);

				localStorage.setItem('tenants', JSON.stringify(_mapTenants(action.payload)));
			}),
			map(([action]) => action.payload[0]?.token),
			filter((r) => !!r),
			map((token) => SetAuthTokenToStore(token)),
		);
	});

	getTenantsFromStorage$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(ValidateAuthTokenSuccess),
			map(() => JSON.parse(localStorage.getItem('tenants'))),
			filter((r) => !!r),
			map((tenants) => authentication.authtokensuccess(tenants)),
		);
	});

	unsetTokenOnLogout$ = createEffect(
		() => {
			return this.actions$.pipe(
				ofType(authentication.logoutaccountsuccesss),
				concatLatestFrom(() => this.subdomainType$),
				map(([, subdomain]) => subdomain),
				tap(() => localStorage.clear()),
				map((subdomain) =>
					this.router.navigate(['/login'], {
						queryParams: {
							_subdomain: subdomain,
						},
					}),
				),
			);
		},
		{ dispatch: false },
	);

	onSignInRedirect$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(authentication.loginsuccess),
				concatLatestFrom(() => this.subdomainType$),
				map(([resp, subdomain]) => {
					// let statusAccount = localStorage.getItem('social_token');
					// if (statusAccount == null) {
					if (resp.payload.length > 1) {
						let currentSubdomain = resp.payload.find((i) => i.subdomain == subdomain);
						if (currentSubdomain) return this.redirectToBzai(currentSubdomain);
						return this.router.navigate(['account', { data: JSON.stringify(resp.payload) }]);
					} else {
						return this.redirectToBzai(resp.payload[0]);
					}
					// }
				}),
			),
		{ dispatch: false },
	);

	onSignInSocial$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(authentication.fblogin),
				map(() => this.authService.signIn(FacebookLoginProvider.PROVIDER_ID)),
			),
		{ dispatch: false },
	);

	onGoogleLogin$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(authentication.googlelogin),
				map(() => this.authService.signIn(GoogleLoginProvider.PROVIDER_ID)),
			),
		{ dispatch: false },
	);

	authStateChange$ = createEffect(() =>
		this.authService.authState.pipe(map((u: SocialUser) => authentication.authstatechanged(u))),
	);

	socialLoginAuth$ = createEffect(() =>
		this.actions$.pipe(
			ofType(authentication.authstatechanged),
			concatLatestFrom(() => this.store.select(getSelectors().selectUrl)),
			filter(([, url]) => url.includes('/login')),
			map(([data]) => ({
				token: data.payload.provider == 'GOOGLE' ? data.payload.idToken : data.payload.authToken,
				provider: data.payload.provider.toLocaleLowerCase(),
			})),
			switchMap((value) => {
				localStorage.setItem('social_token', value.token);
				localStorage.setItem('isLogin', 'true');
				return this.loginService.login(value).pipe(
					map((resp) => authentication.loginsuccess(resp.data)),
					catchError((err) => of(authentication.loginfail(err.message))),
				);
			}),
		),
	);

	loginTenant$ = createEffect(
		() => {
			return this.actions$.pipe(
				ofType(authenticationAccount.loginsubdomain, authenticationAccount.loginlinkedaccount),
				concatLatestFrom(() => [this.store.select(SubdomainList), this.store.select(l7_token)]),
				switchMap(([action, tenants, tokenl7]) => {
					const token = action.payload.token;
					// has token, directly return payload
					if (token) {
						const matched = tenants.find((i) => i.token === token);
						const tenant: Pick<LoginRespModel, 'server' | 'subdomain' | 'token' | 'redirect'> = {
							server: matched?.server,
							subdomain: matched?.subdomain,
							token: matched?.token,
							redirect: matched?.redirect,
						};
						return of(tenant);
					} else {
						const tenant: Pick<LoginRespModel, 'server' | 'subdomain' | 'token' | 'redirect'> = {
							server: undefined,
							subdomain: localStorage.getItem('subdomain'),
							token: tokenl7,
							redirect: undefined,
						};
						return of(tenant);
						/* return this.loginService.getToken(action.payload.signature).pipe(
							map((resp) => resp.data),
							map((data) => {
								const responds: Pick<LoginRespModel, 'server' | 'subdomain' | 'token' | 'redirect'> = {
									token: data?.token,
									server: data?.server,
									subdomain: action.payload.subdomain,
									redirect: action.payload.redirect,
								};
								return responds;
							}),
						); */
					}
				}),
				tap((tenant) => this.redirectToBzai(tenant)),
				catchError((err) => of(authentication.loginfail(err.message))),
			);
		},
		{ dispatch: false },
	);

	authFailed$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(authentication.loginfail),
				map((action) => {
					this._snackBar.openFromComponent(SnackbarErrorComponent, {
						data: action.error,
						panelClass: 'bg-danger',
						// duration: 3000,
					});
				}),
			),
		{ dispatch: false },
	);

	redirectToBzai(data: Pick<LoginRespModel, 'server' | 'subdomain' | 'token' | 'redirect'>) {
		const v6 = data.server ? `/v2` : '';
		const subdomain = data.redirect ?? data.subdomain;
		const uri = `${base_url}${subdomain}${redirect_url}${v6}/home?token=${data.token}`;
		return (window.location.href = uri);
	}
}
function _mapTenants(payload: LoginRespModel[]): TenantAccount[] {
	return payload.map((i) => ({
		companyName: i.company_name,
		signature: i.signature,
		subdomain: i.subdomain,
		subdomain_type: i.subdomain_type,
		token: i.token,
		redirect: i.redirect,
		server: i.server,
	}));
}
